'use client';

import type { INotification, ExportReadyMetadata } from 'src/types/notification';
import type { IconButtonProps } from '@mui/material/IconButton';

import { Virtuoso } from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import { toast } from 'src/components/snackbar';
import { useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import Stack from '@mui/material/Stack';
import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { useBoolean } from 'src/hooks/use-boolean';
import { usePaginatedApi } from 'src/hooks/use-paginated-api';
import { useSSRPaginatedInfiniteScrolling } from 'src/hooks/use-ssr-paginated-infinite-scrolling';

import axios, { endpoints } from 'src/utils/axios';
import { downloadActivityExport } from 'src/actions/visit';
import { downloadInspectorExport } from 'src/actions/inspector';

import { Iconify } from 'src/components/iconify';
import { EntityLinkManager } from 'src/components/EntityLinkManager';

import { NotificationItem } from './notification-item';



export type NotificationPopoverProps = IconButtonProps;

export function NotificationPopover({ sx, ...other }: NotificationPopoverProps) {
  const { t } = useTranslation('notifications');
  
  const popover = useBoolean();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const {
    data: notificationsData,
    page,
    pageSize,
    onPageLoaded,
    onPageChange,
    total,
  } = useSSRPaginatedInfiniteScrolling(100);

  const { data: notifications, pagination: notificationsPagination, extra, mutate } = usePaginatedApi<INotification>({
    endpoint: endpoints.notifications.list,
    paginationParams: {
      page: page + 1,
      take: pageSize,
    }
  });

  useEffect(() => {
    onPageLoaded(notifications, notificationsPagination);
  }, [notifications, notificationsPagination, onPageLoaded]);


  useEffect(() => {
    const interval = setInterval(() => {
      onPageChange(0);
      mutate();
    }, 60000); // Poll notifications every 60 seconds
    return () => clearInterval(interval);
  }, [mutate, onPageChange]);

  const loadMore = useCallback(() => {
    if (notificationsData.length < total) {
      onPageChange(page + 1);
    }
  }, [notificationsData.length, total, page, onPageChange]);


  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    popover.onTrue();
    onPageChange(0);
    mutate();
  };

  const handleClose = () => {
    setAnchorEl(null);
    popover.onFalse();
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
    handleClose();
  };

  const markAsRead = useCallback(async (id: string | number) => {
    try {
      await axios.patch(endpoints.notifications.markAsRead(id));
      mutate();
    } catch (error) {
      console.error(error);
    }
  }, [mutate]);

  const markAllAsRead = useCallback(async () => {
    try {
      await axios.patch(endpoints.notifications.markAllAsRead);
      mutate();
    } catch (error) {
      console.error(error);
    }
  }, [mutate]);

  const [downloadingIds, setDownloadingIds] = useState<Record<string, boolean>>({});

  const handleExportReadyClick = useCallback(
    async (notification: INotification) => {
      const key = String(notification.id);
      const metadata = notification.metadata as ExportReadyMetadata | undefined;
      if (!metadata?.exportId) {
        toast.error(
          t(
            notification.action === 'INSPECTOR_EXPORT_READY'
              ? 'notifications.actions.inspector_export_download_error'
              : 'notifications.actions.activity_export_download_error',
          ),
        );
        return;
      }
      const downloadErrorKey =
        metadata.type === 'INSPECTOR_EXPORT'
          ? 'notifications.actions.inspector_export_download_error'
          : 'notifications.actions.activity_export_download_error';
      let claimed = false;
      setDownloadingIds((prev) => {
        if (prev[key]) return prev;
        claimed = true;
        return { ...prev, [key]: true };
      });
      if (!claimed) return;
      try {
        if (metadata.type === 'INSPECTOR_EXPORT') {
          await downloadInspectorExport(metadata.exportId, metadata.fileName);
        } else {
          await downloadActivityExport(metadata.exportId, metadata.fileName);
        }
        await markAsRead(notification.id);
      } catch (error) {
        console.error('Failed to download export:', error);
        toast.error(t(downloadErrorKey));
      } finally {
        setDownloadingIds((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    },
    [markAsRead, t],
  );

  const handleExportFailedClick = useCallback(
    async (notification: INotification) => {
      await markAsRead(notification.id);
    },
    [markAsRead],
  );

  return (
    <>
      <IconButton
        onClick={handleOpen}
        disableRipple
        sx={{
          width: 40,
          height: 40,
          transition: 'none !important',
          '&:hover': { backgroundColor: 'action.hover' },
          '&:active': { backgroundColor: 'action.hover' },
          backgroundColor: popover.value ? 'action.hover' : undefined,
          ...sx,
        }}
        {...other}
      >
        <Badge
          badgeContent={extra?.unreadCount}
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.75rem',
              height: 20,
              minWidth: 20,
            },
          }}
        >
          <Iconify 
            icon="solar:bell-bing-bold" 
            width={24} 
            sx={{
              color: extra?.unreadCount > 0 ? 'warning.main' : 'text.secondary',
            }}
          />
        </Badge>
      </IconButton>

      <Popover
        open={popover.value}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 400,
              height: 600, 
              boxShadow: (theme) => theme.customShadows.dropdown,
              borderRadius: 2,
              overflow: 'hidden', 
            },
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              py: 1,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              flexShrink: 0, 
            }}
          >
             <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 18 }}>
              {t('notifications.title')}
            </Typography>
            
            <Stack direction="row" spacing={0.5}>
              <IconButton
                size="small"
                onClick={handleMarkAllAsRead}
                disabled={extra?.unreadCount === 0}
                sx={{
                  width: 36,
                  height: 36,
                  color: extra?.unreadCount > 0 ? 'primary.main' : 'text.disabled',
                }}
              >
                <Iconify icon="solar:check-read-linear" width={20} />
              </IconButton>
            </Stack>
          </Box>

          {/* Content */}
          <Box
            sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}
          >
            {!notifications ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <CircularProgress size={40} />
              </Box>
            ) : notificationsData.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  textAlign: 'center',
                  px: 3,
                }}
              >
                <Typography variant="body2" >
                  {t('common:list.empty')}
                </Typography>
              </Box>
            ) : (
              <Virtuoso
                key={notificationsData.length}
                style={{ 
                  height: '100%', 
                  width: '100%',
                }}
                data={notificationsData}
                computeItemKey={(index, notification) => notification.id.toString()}
                defaultItemHeight={80}
                overscan={80}
                endReached={() => {
                  if (notificationsPagination.hasNextPage) {
                    loadMore();
                  }
                }}
                itemContent={(index, notification) => {
                  if (
                    notification.action === 'ACTIVITY_EXPORT_READY' ||
                    notification.action === 'INSPECTOR_EXPORT_READY'
                  ) {
                    return (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onClick={() => handleExportReadyClick(notification)}
                      />
                    );
                  }
                  if (
                    notification.action === 'ACTIVITY_EXPORT_FAILED' ||
                    notification.action === 'INSPECTOR_EXPORT_FAILED'
                  ) {
                    return (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onClick={() => handleExportFailedClick(notification)}
                      />
                    );
                  }
                  return (
                    <EntityLinkManager
                      entityId={notification.entityId}
                      entityType={notification.entityType}
                      onClose={() => markAsRead(notification.id)}
                      label={({ onClick }) => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onClick={onClick}
                        />
                      )}
                    />
                  );
                }}
                components={{
                  Footer: () => (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                       {notificationsPagination.hasNextPage ? (
                        <Typography variant="body2" color="text.secondary">
                           {t('notifications.feed.loadingMore')}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                           {t('notifications.feed.endOfList')}
                        </Typography>
                      )}
                    </Box>
                  ),
                }}
              />
            )}
          </Box>
        </Box>
      </Popover>
    </>
  );
}